import { Grid } from '@material-ui/core'
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord'
import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import _isEmpty from 'lodash/isEmpty'
import PropTypes from 'prop-types'
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { DISPLAY_VBS_NUMBER_FORMAT as NUMBER_FORMAT } from '../../../../../../constants'
import { useFormattingContext } from '../../../../FormattingProvider/FormattingContext'
import EditAccountButton from '../../../../VisualBalanceSheetV2/EditAccountButton'
import { BlockItemShape } from '../propTypes'
import { getFormatString } from '../utils'
import { useVisualBlocksContext } from '../VisualBlocksContext'

const useStyles = makeStyles(() => ({
  tooltipPopover: {
    zIndex: 9999
  },
  tooltip: {
    transform: 'translate(0, -98%)',
    backgroundColor: 'var(--theme-color-surface-d0)',
    color: 'var(--text-color-main)',
    boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.14)',
    borderRadius: 'var(--border-radius-lg)',
    fixedWidth: 'none',
    padding: '20px 30px',
    zIndex: 1,
    position: 'absolute',
    top: 0,
    right: '165px',
    left: 0,
    transition: 'opacity ease-in-out 0.25s'
  },
  tooltipArrow: {
    position: 'absolute',
    width: 0,
    height: 0,
    borderLeft: '1.5rem solid transparent',
    borderRight: '1.5rem solid transparent',
    borderTop: '1.5rem solid var(--theme-color-surface-d0)',
    bottom: '-0.5rem',
    left: '50%',
    transform: 'translate(-50%, 50%)'
  },
  tooltipBelow: {
    transform: 'translate(0, -2%)',
    top: '100%'
  },
  tooltipArrowBelow: {
    transform: 'translate(-24px,-98%) rotate(180deg)',
    bottom: 'auto',
    top: '4px'
  },
  tooltipTitle: {
    textTransform: 'uppercase',
    marginBottom: '12px',
    color: 'var(--text-color-main)',
    fontSize: '0.875rem'
  },
  tooltipItem: {
    marginBottom: '5px'
  },
  tooltipItemTitle: {
    fontSize: '1rem',
    fontWeight: 'normal',
    color: 'var(--text-color-main)',
    position: 'relative'
  },
  tooltipItemSubtitle: {
    fontSize: '1rem',
    color: 'var(--text-color-main)',
    textAlign: 'right'
  },
  tooltipItemHovered: {
    fontWeight: 'bold',
    color: 'var(--text-color-main) !important'
  }
}))

export const BlockItemSummaryTooltip = ({
  color,
  details,
  depth,
  itemHovered,
  visible = false
}) => {
  const { formatter } = useFormattingContext()
  const { formats, enableManualAccountManagement } = useVisualBlocksContext()
  const tooltipRef = useRef(null)

  const formatString = getFormatString(formats, depth, 'tooltips')

  const [showBelow, setShowBelow] = useState(true)
  const classes = useStyles()

  const checkTooltipVisibility = useCallback(() => {
    if (!tooltipRef.current || !visible) return

    const containerBlock = tooltipRef.current.closest("[data-id='visual-blocks-container']")
    const parentBlock = tooltipRef.current.parentElement

    setShowBelow(
      parentBlock.getBoundingClientRect().top - tooltipRef.current.offsetHeight < containerBlock.getBoundingClientRect().top
    )
  }, [visible])

  // Use layoutEffect to measure synchronously before paint, avoiding flash
  useLayoutEffect(() => {
    if (!tooltipRef.current || !visible) {
      return
    }
    checkTooltipVisibility()
    const handleUpdate = () => {
      requestAnimationFrame(() => {
        checkTooltipVisibility()
      })
    }
    window.addEventListener('scroll', handleUpdate, true)
    window.addEventListener('resize', handleUpdate)

    return () => {
      window.removeEventListener('scroll', handleUpdate, true)
      window.removeEventListener('resize', handleUpdate)
    }
  }, [visible, checkTooltipVisibility])

  const middle = useMemo(() => Math.ceil(details.length / 2), [details])
  const columns = useMemo(
    () =>
      details.reduce(
        (acc, block, index) => {
          if (index < middle) {
            return { ...acc, firstColumn: [...acc.firstColumn, block] }
          }
          return { ...acc, secondColumn: [...acc.secondColumn, block] }
        },
        { firstColumn: [], secondColumn: [] }
      ),
    [details, middle]
  )

  const isHovered = useCallback(
    (id) => {
      if (!itemHovered) return false
      if (itemHovered.startsWith('_remaining_')) {
        return itemHovered.split(',').includes(id)
      }
      return itemHovered === id
    },
    [itemHovered]
  )

  return (
    <>
      <div
        style={{
          visibility: visible ? 'visible' : 'hidden',
          opacity: visible ? 1 : 0,
          display: visible ? 'block' : 'none'
        }}
        ref={tooltipRef}
        className={clsx(classes.tooltip, showBelow && classes.tooltipBelow)}
      >
        <div
          className={clsx(
            classes.tooltipArrow,
            showBelow && classes.tooltipArrowBelow
          )}
        />
        <Grid container spacing={4}>
          {Object.values(columns).map((column, index) => {
            if (_isEmpty(column)) {
              return null
            }

            const hasMultipleColumns = columns.firstColumn.length > 1
            return (
              <Grid
                item
                lg={hasMultipleColumns ? 6 : 12}
                xs={12}
                key={`tooltip-column-${index + 1}`}
              >
                {column.map((block) => {
                  return (
                    <div className={classes.tooltipItem} key={block.id}>
                      <Grid container spacing={2}>
                        <Grid
                          item
                          xs={12}
                          className={clsx(classes.tooltipItemTitle, {
                            [classes.tooltipItemHovered]: isHovered(block.id)
                          })}
                        >
                          <div className='flex items-center relative gap-2'>
                            {enableManualAccountManagement && block.accountId && (
                              <EditAccountButton accountId={block.accountId} />
                            )}
                            <div>
                              <FiberManualRecordIcon
                                style={{
                                  fontSize: 8,
                                  color,
                                  opacity: isHovered(block.id) ? 1 : 0.5
                                }}
                              />
                            </div>
                            <div className='flex-1'>{block.label}</div>
                            <div>
                              {formatter(
                                block.value,
                                formatString || NUMBER_FORMAT
                              )}
                            </div>
                          </div>
                        </Grid>
                      </Grid>
                    </div>
                  )
                })}
              </Grid>
            )
          })}
        </Grid>
      </div>
    </>
  )
}

BlockItemSummaryTooltip.propTypes = {
  visible: PropTypes.bool,
  color: PropTypes.string.isRequired,
  details: PropTypes.arrayOf(BlockItemShape),
  depth: PropTypes.number.isRequired,
  itemHovered: PropTypes.string
}
